import { Image } from 'expo-image';
import * as React from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
  type LayoutChangeEvent,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BrandBackdrop } from '@/components/brand/brand-backdrop';
import { brandAssets, mediaAssets } from '@/lib/brand-assets';

const HORIZONTAL_LOGO_ASPECT_RATIO = 1600 / 347;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getAuthLayout(width: number, height: number, topInset: number, bottomInset: number) {
  const horizontalPadding = clamp(width * 0.055, 18, 28);
  const contentWidth = width - horizontalPadding * 2;
  const logoWidth = clamp(contentWidth * 0.72, 218, 292);
  const compact = height < 760;

  return {
    bottomPadding: bottomInset + (compact ? 14 : 24),
    horizontalPadding,
    logoHeight: logoWidth / HORIZONTAL_LOGO_ASPECT_RATIO,
    logoTopPadding: topInset + (compact ? 18 : 28),
    logoWidth,
    panelMaxWidth: 440,
    panelTopPadding: compact ? 28 : 48,
  };
}

function AuthScreenShell({ children }: Readonly<{ children: React.ReactNode }>) {
  const { height, width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // The window is not the space this screen actually gets: on Android the status
  // bar strip has already taken its share above us. Measuring keeps the compact
  // layout from being picked off a height that is always slightly too generous.
  const [availableHeight, setAvailableHeight] = React.useState(height);
  const layout = getAuthLayout(
    width,
    availableHeight,
    process.env.EXPO_OS === 'web' ? insets.top : 0,
    insets.bottom
  );

  const measure = React.useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    setAvailableHeight((current) => (Math.abs(current - measured) > 1 ? measured : current));
  }, []);

  return (
    <BrandBackdrop
      source={mediaAssets.homeViewBg}
      initialBlurIntensity={28}
      blurTint="light"
      scrimOpacity={0.58}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        className="flex-1"
        onLayout={measure}
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
          contentInsetAdjustmentBehavior="automatic"
          automaticallyAdjustKeyboardInsets
          className="flex-1"
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: layout.bottomPadding + 36,
            paddingHorizontal: layout.horizontalPadding,
            paddingTop: layout.logoTopPadding,
          }}
        >
          <View className="w-full flex-1 items-center">
            <Image
              source={brandAssets.logoReverse}
              contentFit="contain"
              style={{
                height: layout.logoHeight,
                width: layout.logoWidth,
              }}
            />
            <View
              className="w-full flex-1 justify-center"
              style={{
                maxWidth: layout.panelMaxWidth,
                paddingTop: layout.panelTopPadding,
              }}
            >
              {children}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </BrandBackdrop>
  );
}

export { AuthScreenShell };
